import React, { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import { Box, Typography, IconButton, Chip, Tooltip } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EmailIcon from "@mui/icons-material/Email";

// Step type icons
import InputIcon from "@mui/icons-material/Input";
import PaymentIcon from "@mui/icons-material/Payment";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DrawIcon from "@mui/icons-material/Draw";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import BadgeIcon from "@mui/icons-material/Badge";
import OutputIcon from "@mui/icons-material/Output";

export interface StepNodeData extends Record<string, unknown> {
  stepIndex: number;
  stepType: string;
  stepName: string;
  executors: string[];
  notificationType?: string;
  onEdit: (stepIndex: number) => void;
  onDelete: (stepIndex: number) => void;
}

const stepTypeConfig: Record<
  string,
  { icon: React.ReactNode; color: string; label: string }
> = {
  DATA_INPUT: {
    icon: <InputIcon />,
    color: "#2196F3",
    label: "Duomenų įvedimas",
  },
  PAYMENT: {
    icon: <PaymentIcon />,
    color: "#4CAF50",
    label: "Apmokėjimas",
  },
  STRIPE_PAYMENT: {
    icon: <CreditCardIcon />,
    color: "#6772E5",
    label: "Stripe mokėjimas",
  },
  SIGN: {
    icon: <DrawIcon />,
    color: "#9C27B0",
    label: "Pasirašymas",
  },
  GENERATE_DOCUMENT: {
    icon: <DescriptionIcon />,
    color: "#FF9800",
    label: "Dokumento generavimas",
  },
  NOTIFICATION: {
    icon: <NotificationsIcon />,
    color: "#F44336",
    label: "Pranešimas",
  },
  CONFIRMATION: {
    icon: <CheckCircleIcon />,
    color: "#FFC107",
    label: "Patvirtinimas",
  },
  IDENTIFICATION: {
    icon: <BadgeIcon />,
    color: "#616161",
    label: "Identifikavimas",
  },
  DATA_OUTPUT: {
    icon: <OutputIcon />,
    color: "#00BCD4",
    label: "Duomenų išvedimas",
  },
};

function StepNode({ data, selected }: { data: StepNodeData; selected?: boolean }) {
  const config = stepTypeConfig[data.stepType] || {
    icon: <InputIcon />,
    color: "#757575",
    label: data.stepType,
  };

  return (
    <Box
      sx={{
        position: "relative",
        minWidth: 280,
        bgcolor: "white",
        borderRadius: 2,
        border: selected ? `3px solid ${config.color}` : "2px solid #e0e0e0",
        boxShadow: selected
          ? `0 8px 24px ${config.color}40`
          : "0 2px 8px rgba(0,0,0,0.1)",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: `0 4px 16px ${config.color}30`,
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Top handle for incoming connections */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          width: 12,
          height: 12,
          background: config.color,
          border: "2px solid white",
        }}
      />

      {/* Header with drag handle and step number */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: `${config.color}15`,
          px: 2,
          py: 1,
          borderTopLeftRadius: 6,
          borderTopRightRadius: 6,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DragIndicatorIcon
            sx={{ color: config.color, cursor: "grab" }}
            className="drag-handle"
          />
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: config.color,
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: "0.875rem",
            }}
          >
            {data.stepIndex + 1}
          </Box>
          <Box sx={{ color: config.color, display: "flex" }}>
            {config.icon}
          </Box>
        </Box>
      </Box>

      {/* Step name and type */}
      <Box sx={{ px: 2, py: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 0.5,
            color: "text.primary",
            wordBreak: "break-word",
          }}
        >
          {data.stepName || config.label}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {config.label}
        </Typography>
      </Box>

      {/* Executors and notification info */}
      {(data.executors?.length > 0 || data.notificationType !== "NONE") && (
        <Box sx={{ px: 2, pb: 1.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {data.executors?.map((executor, idx) => (
            <Chip
              key={idx}
              label={executor}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.7rem",
                bgcolor: `${config.color}20`,
                color: config.color,
                fontWeight: 600,
              }}
            />
          ))}
          {data.notificationType && data.notificationType !== "NONE" && (
            <Tooltip title={`Pranešimas: ${data.notificationType}`}>
              <Chip
                icon={<EmailIcon sx={{ fontSize: 14 }} />}
                label={data.notificationType}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  bgcolor: "#1976d220",
                  color: "#1976d2",
                }}
              />
            </Tooltip>
          )}
        </Box>
      )}

      {/* Action buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 0.5,
          px: 1,
          pb: 1,
          borderTop: "1px solid #f0f0f0",
          pt: 1,
        }}
      >
        <Tooltip title="Redaguoti">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              data.onEdit(data.stepIndex);
            }}
            sx={{
              color: config.color,
              "&:hover": { bgcolor: `${config.color}15` },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Ištrinti">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              data.onDelete(data.stepIndex);
            }}
            sx={{
              color: "error.main",
              "&:hover": { bgcolor: "error.light" },
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Bottom handle for outgoing connections */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          width: 12,
          height: 12,
          background: config.color,
          border: "2px solid white",
        }}
      />
    </Box>
  );
}

export default memo(StepNode);

// Export step type config for use in other components
export { stepTypeConfig };
